import React from "react";

import { PaymentStatusDto } from "services/organizationBranches/restModels";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { EmptyState } from "shared/ui/EmptyState";
import { LabeledField } from "shared/ui/LabeledField";
import { NotificationSnackbar } from "shared/ui/NotificationSnackbar";
import { Pagination } from "shared/ui/Pagination";
import { SearchInput } from "shared/ui/SearchInput";
import { Select } from "shared/ui/Select";
import { Skeleton } from "shared/ui/Skeleton";
import { TextInput } from "shared/ui/TextInput";

import { BranchesTable } from "./components/BranchesTable";
import { PAYMENT_STATUS_OPTIONS } from "./constants";
import { useBranchesRegistryState } from "./hooks/useBranchesRegistryState";
import { Filters } from "./styles";

export const BranchesRegistry = () => {
    const state = useBranchesRegistryState();

    const renderBody = () => {
        if (state.error) {
            return (
                <Banner tone="danger" title="Не удалось загрузить список">
                    <Button size="small" variant="outlined" color="neutral" onClick={state.onRetry}>
                        Повторить
                    </Button>
                </Banner>
            );
        }

        // Скелетон только на первой загрузке: при смене фильтра список уже есть на экране,
        // и подмена его заглушкой перестраивала бы раскладку на каждую букву поиска.
        if (state.loading && state.items.length === 0) {
            return <Skeleton height="320px" />;
        }

        if (state.items.length === 0) {
            return (
                <EmptyState
                    title="Ничего не найдено"
                    description={
                        state.hasFilters
                            ? "Попробуйте изменить фильтры"
                            : "Пока нет ресторанов"
                    }
                    action={
                        state.hasFilters ? (
                            <Button variant="outlined" color="neutral" onClick={state.onResetFilters}>
                                Сбросить фильтры
                            </Button>
                        ) : undefined
                    }
                />
            );
        }

        return (
            <BranchesTable
                items={state.items}
                busyBranchId={state.busyBranchId}
                onOpenAsClient={state.onOpenAsClient}
                onCopySignInLink={state.onCopySignInLink}
                footer={
                    <Pagination
                        page={state.page}
                        pageSize={state.pageSize}
                        total={state.total}
                        onChange={state.onChangePage}
                    />
                }
            />
        );
    };

    return (
        <>
            <Filters>
                <LabeledField label="Поиск по названию">
                    <SearchInput
                        value={state.query}
                        onChange={state.onChangeQuery}
                        placeholder="Название заведения"
                    />
                </LabeledField>

                <LabeledField label="ID заведения" htmlFor="branchId">
                    <TextInput
                        id="branchId"
                        value={state.branchId}
                        onChange={state.onChangeBranchId}
                        placeholder="Например, 128"
                    />
                </LabeledField>

                <LabeledField label="Статус оплаты">
                    <Select
                        value={state.paymentStatus || ""}
                        options={PAYMENT_STATUS_OPTIONS}
                        title="Статус оплаты"
                        onChange={(value) =>
                            state.onChangePaymentStatus((value || null) as PaymentStatusDto | null)
                        }
                    />
                </LabeledField>

                <Button
                    variant="outlined"
                    color="neutral"
                    onClick={state.onResetFilters}
                    disabled={!state.hasFilters}
                >
                    Сбросить
                </Button>
            </Filters>

            {renderBody()}

            <NotificationSnackbar {...state.notify.snackbarProps} />
        </>
    );
};
